import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Command } from "cmdk";

const CmdInput = Command.Input, CmdList = Command.List, CmdEmpty = Command.Empty, CmdGroup = Command.Group, CmdItem = Command.Item;
import { ArrowRight, ClipboardList, Clock, FileText, Package, Search, Store, Users, Wine, X } from "lucide-react";
import { recents, remember, type Recent } from "./recent";
import { api } from "../../lib/api";
import { HUBS } from "./catalogue/catalogueNav";

interface Hit { id: string; title: string; sub: string; to: string }
interface Groups { groups: { key: string; label: string; items: Hit[] }[] }
interface Page { title: string; sub: string; to: string; words: string }
interface Section { key: string; label: string }

const PRINT_TILES = [["Tasting notes", "print sheets per wine, with a cover", "notes"], ["Tasting events", "event list, QR ordering, score sheets", "events"], ["Product cards", "shelf tags, single or A4 sheet of ten", "cards"], ["Letters", "branded letters queued by order tag", "letters"]];
const ICONS: Record<string, React.ComponentType<{ className?: string }>> = { recent: Clock, pages: FileText, tasks: ClipboardList, orders: Package, customers: Users, wines: Wine, suppliers: Store };

function pages(keys: string[], SECTIONS: readonly Section[]): Page[] {
  const allowed = new Set(keys);
  const hubs = HUBS.filter((h) => allowed.has(h.key));
  const viaHub = new Set(hubs.flatMap((h) => h.tabs.map((t) => t.section as string)));
  const out: Page[] = SECTIONS.filter((s) => (allowed.has(s.key) || viaHub.has(s.key)) && !HUBS.some((h) => h.key === s.key)).map((s) => ({ title: s.label, sub: "page", to: `/admin/${s.key}`, words: s.label }));
  for (const h of hubs) for (const t of h.tabs) out.push({ title: `${h.label} · ${t.label}`, sub: t.purpose.split(" — ")[0].slice(0, 90), to: t.to, words: `${h.label} ${t.label} ${t.purpose}` });
  if (allowed.has("print")) for (const [t, sub, v] of PRINT_TILES) out.push({ title: `Print activities · ${t}`, sub, to: `/admin/print?view=${v}`, words: `print ${t} ${sub}` });
  return out;
}

/** ⌘K / Ctrl-K: find a page, a task, a purchase order, a customer, a wine or a supplier from anywhere in the console. */
export function CommandPalette({ keys, sections }: { keys: string[]; sections: readonly Section[] }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState<Recent[]>(() => recents());
  useEffect(() => {
    const onRecent = () => setRecent(recents());
    window.addEventListener("console-recent-changed", onRecent);
    return () => window.removeEventListener("console-recent-changed", onRecent);
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); } };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey); window.addEventListener("open-console-search", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("open-console-search", onOpen); };
  }, []);
  useEffect(() => { if (!open) setQ(""); }, [open]);
  const all = useMemo(() => pages(keys, sections), [keys, sections]);
  const term = q.trim().toLowerCase();
  const pageHits = useMemo(() => (term ? all.filter((p) => p.words.toLowerCase().includes(term)) : all).slice(0, term ? 8 : 12), [all, term]);
  const { data, isFetching } = useQuery<Groups>({ queryKey: ["console-search", term], queryFn: () => api.get("/admin/search", { params: { q: term } }).then((r) => r.data), enabled: open && term.length >= 2, staleTime: 30000 });
  const recentHits = useMemo(() => recent.filter((r) => !term || `${r.title} ${r.sub}`.toLowerCase().includes(term)).slice(0, 5), [recent, term]);
  const go = (to: string, hit?: Hit, k?: string) => { if (hit && k && k !== "recent") remember({ k, id: hit.id, title: hit.title, sub: hit.sub, to }); setOpen(false); navigate(to); };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-4 pt-[10vh] backdrop-blur-[2px]" onMouseDown={() => setOpen(false)} data-testid="console-search">
      <Command shouldFilter={false} className="w-full max-w-2xl overflow-hidden border border-border bg-white shadow-2xl" onMouseDown={(e) => e.stopPropagation()} label="Search the console">
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="h-4 w-4 text-neutral-500" />
          <CmdInput autoFocus value={q} onValueChange={setQ} placeholder="Find a page, task, order, customer, wine or supplier…" className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400" data-testid="console-search-input" />
          <kbd className="hidden border border-border px-1.5 py-0.5 text-[10px] text-neutral-500 sm:inline">esc</kbd>
          <button type="button" onClick={() => setOpen(false)} className="p-1 text-neutral-500" aria-label="Close" data-testid="console-search-close"><X className="h-4 w-4" /></button>
        </div>
        <CmdList className="max-h-[60vh] overflow-y-auto p-2">
          <CmdEmpty className="p-4 text-sm text-neutral-500" data-testid="console-search-empty">{isFetching ? "Searching…" : term.length < 2 ? "Type at least two letters." : `Nothing matches “${q}”.`}</CmdEmpty>
          {recentHits.length > 0 && <Group label="Recent" k="recent" items={recentHits.map((r) => ({ id: r.to, title: r.title, sub: r.sub, to: r.to }))} go={go} />}
          {pageHits.length > 0 && <Group label={term ? "Pages" : "Go to"} k="pages" items={pageHits.map((p) => ({ id: p.to, title: p.title, sub: p.sub, to: p.to }))} go={go} />}
          {(data?.groups || []).map((g) => <Group key={g.key} label={g.label} k={g.key} items={g.items} go={go} />)}
        </CmdList>
        <div className="flex items-center gap-3 border-t border-border px-3 py-1.5 text-[10px] uppercase tracking-widest text-neutral-500"><span>↑↓ move</span><span>↵ open</span><span className="ml-auto">⌘K / Ctrl-K from anywhere</span></div>
      </Command>
    </div>
  );
}

function Group({ label, k, items, go }: { label: string; k: string; items: Hit[]; go: (to: string, hit?: Hit, k?: string) => void }) {
  const Icon = ICONS[k] || FileText;
  return (
    <CmdGroup heading={<span className="eyebrow block px-2 pb-1 pt-2">{label}</span>} data-testid={`console-search-group-${k}`}>
      {items.map((i) => (
        <CmdItem key={`${k}-${i.id}`} value={`${k}-${i.id}`} onSelect={() => go(i.to, i, k)} className="flex cursor-pointer items-center gap-3 px-2 py-2 text-sm aria-selected:bg-[var(--parchment)] data-[selected=true]:bg-[var(--parchment)]" data-testid={`console-search-item-${k}-${i.id}`}>
          <Icon className="h-4 w-4 shrink-0 text-[var(--wine)]" />
          <span className="min-w-0 flex-1"><span className="block truncate">{i.title}</span>{i.sub && <span className="block truncate text-xs text-neutral-500">{i.sub}</span>}</span>
          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-neutral-300" />
        </CmdItem>
      ))}
    </CmdGroup>
  );
}

